import {
  CLOCK_CHOICES,
  DUEL_LEVELS,
  SPECTATOR_SEAT,
  isValidJoinCode,
  normalizeJoinCode,
  type BoardDef,
  type Lobby,
  type LobbySetup,
  type RoomSeat,
} from '@archon3d/core';
import { useState } from 'react';
import { useGameLibrary, gameDefinition } from '../../editor/gameLibrary';
import type { ServerStatus } from '../../net/serverClient';
import type { RoomController, RoomUiState } from '../../net/useRoom';
import { fitSetup, humanSeat, type GameSetup } from '../gameSetup';
import { AI_DEPTHS } from '../settings';
import { GameSetupPanel } from './GameSetupPanel';
import { serverLabel } from './serverLabel';

const NAME_KEY = 'archon3d.name';

function loadName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // The name lasts for this visit.
  }
}

/**
 * The room's plan from the local setup: the same board (a bundled one; rooms know no saved
 * boards) and every playing seat open for a friend; the host marks a seat AI on purpose, since
 * open seats nobody takes go to the AI anyway. People at one device make no sense online, so
 * that is off.
 */
function hostSetupFrom(setup: GameSetup, boards: BoardDef[]): GameSetup {
  const board = boards.find((b) => b.id === setup.boardId) ?? boards[0]!;
  return fitSetup(
    {
      ...setup,
      boardId: board.id,
      seats: board.factions.map((_, i) => (i < 2 ? humanSeat() : { kind: 'none' as const })),
      oneDevice: false,
    },
    board,
  );
}

function roomSeatsOf(setup: GameSetup): RoomSeat[] {
  return setup.seats.map((s): RoomSeat => {
    if (s.kind === 'human') return { kind: 'open' };
    if (s.kind === 'ai') return { kind: 'ai', depth: s.depth, duel: s.duel };
    return { kind: 'none' };
  });
}

interface HostProps {
  server: ServerStatus;
  boards: BoardDef[];
  /** The local setup, for the board, the seats and the fights to start from. */
  setup: GameSetup;
  room: RoomController;
  onBack: () => void;
}

/** Opens a room on the server: the code goes to friends, open seats nobody takes go to the AI. */
export function HostPanel({ server, setup: local, room, onBack }: HostProps) {
  const library = useGameLibrary();
  const boards = library.map((g) => g.definition.board);
  const [name, setName] = useState(loadName);
  const [setup, setSetup] = useState(() => hostSetupFrom(local, boards));
  const [hostSeat, setHostSeat] = useState(() =>
    Math.max(
      0,
      setup.seats.findIndex((s) => s.kind === 'human'),
    ),
  );
  const [clockSeconds, setClockSeconds] = useState(0);
  const online = server.state === 'online';

  if (room.state.phase !== 'idle')
    return <RoomStatus state={room.state} boards={boards} room={room} onBack={onBack} />;

  const board = boards.find((b) => b.id === setup.boardId) ?? boards[0]!;
  const seat =
    setup.seats[hostSeat]?.kind === 'human'
      ? hostSeat
      : setup.seats.findIndex((s) => s.kind === 'human');
  return (
    <GameSetupPanel
      mode="host"
      boards={boards}
      savedBoards={[]}
      setup={setup}
      onSetup={setSetup}
      hostSeat={seat}
      onHostSeat={setHostSeat}
      before={
        <>
          <NameRow name={name} onName={setName} />
          <ClockChoice value={clockSeconds} onChange={setClockSeconds} />
        </>
      }
      onBack={onBack}
      onStart={() => {
        saveName(name);
        room.create({
          boardId: board.id,
          definition: board.id === 'classic-9x9' ? undefined : gameDefinition(board.id),
          players: setup.seats.filter((s) => s.kind !== 'none').length,
          seat: Math.max(0, seat),
          name,
          seats: roomSeatsOf(setup),
          fights: setup.fights,
          humanFriendlyAi: setup.humanFriendlyAi ?? true,
          engagement: setup.engagement,
          clockSeconds,
        });
      }}
      startLabel="Create room"
      startTestId="host-create"
      startDisabled={!online || seat < 0}
      hints={[
        'Hosting opens a room on the game server and gives you a six-letter code for your friends.',
        serverLabel(server),
      ]}
      error={room.state.error}
    />
  );
}

interface JoinProps {
  server: ServerStatus;
  boards: BoardDef[];
  room: RoomController;
  onBack: () => void;
}

/** Enters a friend's room by its code and waits in the lobby for the host to start. */
export function JoinPanel({ server, boards, room, onBack }: JoinProps) {
  const [name, setName] = useState(loadName);
  const [code, setCode] = useState('');
  const [watch, setWatch] = useState(false);
  const online = server.state === 'online';
  const valid = isValidJoinCode(code);

  if (room.state.phase !== 'idle')
    return <RoomStatus state={room.state} boards={boards} room={room} onBack={onBack} />;

  const submit = () => {
    if (!online || !valid) return;
    saveName(name);
    room.join(code, name, watch);
  };

  return (
    <>
      <h2 className="menu-heading">Join game</h2>
      <p className="menu-text">Enter the code your host reads out.</p>
      <NameRow name={name} onName={setName} />
      <WatchChoice watch={watch} onChange={setWatch} />
      <div className="menu-row">
        <span className="menu-label">Code</span>
        <input
          className="code"
          value={code}
          maxLength={6}
          placeholder="ABCDEF"
          autoCapitalize="characters"
          data-testid="join-code"
          onChange={(e) => setCode(normalizeJoinCode(e.target.value).replace(/[^A-Z0-9]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
      </div>
      <p className="hint">{serverLabel(server)}</p>
      {room.state.error && <p className="hint warn">{room.state.error}</p>}
      <div className="menu-actions">
        <button onClick={onBack}>Back</button>
        <button
          className="primary"
          disabled={!online || !valid}
          onClick={submit}
          data-testid="join-submit"
        >
          {watch ? 'Watch' : 'Join'}
        </button>
      </div>
    </>
  );
}

/** The host's chess clock: seconds per move, or none. Duels are never clocked. */
function ClockChoice({ value, onChange }: { value: number; onChange: (seconds: number) => void }) {
  return (
    <div className="menu-row">
      <span className="menu-label">Clock</span>
      <div className="choice" role="radiogroup" aria-label="Seconds per move">
        {CLOCK_CHOICES.map((seconds) => (
          <button
            key={seconds}
            role="radio"
            aria-checked={value === seconds}
            className={value === seconds ? 'active' : ''}
            onClick={() => onChange(seconds)}
            data-testid={`host-clock-${seconds}`}
          >
            {seconds === 0 ? 'Off' : seconds < 60 ? `${seconds} s` : `${seconds / 60} min`}
          </button>
        ))}
      </div>
    </div>
  );
}

/** "30 s per move" for the lobby, or that there is no clock. */
export function clockLabel(seconds: number): string {
  if (seconds === 0) return 'No clock';
  return `${seconds < 60 ? `${seconds} s` : `${seconds / 60} min`} per move`;
}

/** Take a seat when one is free, or only watch. */
function WatchChoice({ watch, onChange }: { watch: boolean; onChange: (watch: boolean) => void }) {
  return (
    <div className="menu-row">
      <span className="menu-label">I want to</span>
      <div className="choice" role="radiogroup" aria-label="Play or watch">
        {[false, true].map((only) => (
          <button
            key={String(only)}
            role="radio"
            aria-checked={watch === only}
            className={watch === only ? 'active' : ''}
            onClick={() => onChange(only)}
            data-testid={only ? 'join-watch' : 'join-play'}
          >
            {only ? 'Watch' : 'Play'}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The people without a seat, listed under the seats. */
function SpectatorList({ lobby, seat }: { lobby: Lobby; seat: number }) {
  const watchers = lobby.members.filter((m) => m.seat === SPECTATOR_SEAT);
  if (watchers.length === 0) return null;
  return (
    <div className="menu-row" data-testid="lobby-spectators">
      <span className="menu-label">Watching</span>
      <span className="menu-value">
        {watchers.map((m, i) => (
          <span key={i}>
            {i > 0 && ', '}
            {m.name}
            {!m.connected && <span className="muted"> (away)</span>}
          </span>
        ))}
        {seat === SPECTATOR_SEAT && <span className="muted"> · you are watching</span>}
      </span>
    </div>
  );
}

function NameRow({ name, onName }: { name: string; onName: (name: string) => void }) {
  return (
    <div className="menu-row">
      <span className="menu-label">Your name</span>
      <input
        className="name"
        value={name}
        maxLength={24}
        placeholder="Anonymous"
        data-testid="player-name"
        onChange={(e) => onName(e.target.value)}
      />
    </div>
  );
}

interface StatusProps {
  state: Exclude<RoomUiState, { phase: 'idle' }>;
  boards: BoardDef[];
  room: RoomController;
  onBack: () => void;
}

/** What a lobby row says about a seat nobody sits in. */
function emptySeatLabel(seat: RoomSeat | undefined): string {
  if (!seat || seat.kind === 'open') return 'Open · AI if nobody comes';
  if (seat.kind === 'none') return 'Sits out';
  const depth = AI_DEPTHS.find((d) => d.depth === seat.depth)?.label;
  const duel = DUEL_LEVELS.find((d) => d.id === seat.duel)?.label;
  return `AI${depth ? ` · board ${depth.toLowerCase()}` : ''}${duel ? ` · duel ${duel.toLowerCase()}` : ''}`;
}

function fightsLabel(setup: LobbySetup): string {
  switch (setup.fights ?? 'card') {
    case 'watch':
      return 'The AI fights every duel; everyone watches';
    case 'roll':
      return 'Captures are rolled';
    case 'card':
      return 'Captures use Card combat';
    default:
      return `Captures are fought in the arena · Human-friendly AI ${setup.humanFriendlyAi === false ? 'Off' : 'On'}`;
  }
}

/** The lobby while the room fills, or a word about the room's game already being on. */
function RoomStatus({ state, boards, room, onBack }: StatusProps) {
  const leave = () => {
    room.leave();
    onBack();
  };
  if (state.phase === 'busy')
    return (
      <>
        <h2 className="menu-heading">
          {state.action === 'create'
            ? 'Creating the room…'
            : state.action === 'join'
              ? 'Joining…'
              : 'Finding your game…'}
        </h2>
        <div className="menu-actions">
          <button onClick={leave}>Cancel</button>
        </div>
      </>
    );
  if (state.phase === 'playing')
    return (
      <>
        <h2 className="menu-heading">Room {state.code}</h2>
        <p className="menu-text">
          {state.seat === SPECTATOR_SEAT
            ? 'You are watching the game on the table.'
            : 'Your online game is on the table.'}
        </p>
        <div className="menu-actions">
          <button onClick={leave}>Leave game</button>
          <button className="primary" onClick={onBack}>
            Back
          </button>
        </div>
      </>
    );
  if (state.phase === 'replay')
    return (
      <>
        <h2 className="menu-heading">Replay</h2>
        <p className="menu-text">
          A finished game is playing back on the table; the controls are in the banner.
        </p>
        <div className="menu-actions">
          <button onClick={leave}>Stop replay</button>
          <button className="primary" onClick={onBack}>
            Back
          </button>
        </div>
      </>
    );

  const { lobby, seat, host } = state;
  const board = lobby.setup.definition?.board ?? boards.find((b) => b.id === lobby.setup.boardId);
  const sides = lobby.setup.seats?.length ?? lobby.setup.players;
  return (
    <>
      <h2 className="menu-heading">
        {host ? 'Your room' : seat === SPECTATOR_SEAT ? 'Watching' : 'Lobby'}
      </h2>
      <p className="code-display" data-testid="lobby-code" aria-label="Join code">
        {state.code}
      </p>
      <p className="hint">
        {host
          ? 'Read this code to your friends. Open seats nobody takes are played by the AI.'
          : `${board?.name ?? lobby.setup.boardId} · waiting for the host to start.`}
        {` · ${clockLabel(lobby.setup.clockSeconds)} · ${fightsLabel(lobby.setup)}.`}
      </p>
      <ul className="lobby" data-testid="lobby-members">
        {Array.from({ length: sides }, (_, i) => {
          const plan = lobby.setup.seats?.[i];
          const member = lobby.members.find((m) => m.seat === i);
          const faction = board?.factions[i];
          return (
            <li key={i} className={plan?.kind === 'none' ? 'menu-row muted' : 'menu-row'}>
              <span className="menu-label">
                <span className="swatch" style={{ background: faction?.color ?? '#888' }} />
                {i + 1}. {faction?.name ?? `Seat ${i + 1}`}
              </span>
              <span className="menu-value">
                {member ? member.name || `Player ${i + 1}` : emptySeatLabel(plan)}
                {member?.seat === seat && <span className="muted"> (you)</span>}
                {member?.host && <span className="muted"> · host</span>}
                {member && !member.connected && <span className="muted"> · away</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <SpectatorList lobby={lobby} seat={seat} />
      {state.error && <p className="hint warn">{state.error}</p>}
      <div className="menu-actions">
        <button onClick={leave}>Leave</button>
        {host ? (
          <button className="primary" onClick={() => room.start()} data-testid="lobby-start">
            Start
          </button>
        ) : (
          <button className="primary" onClick={onBack}>
            Back
          </button>
        )}
      </div>
    </>
  );
}
